import React, { useEffect, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import { useAsyncError } from "sprint-common-web";

import { PreviewLoadingError } from "../PreviewLoadingError";

export interface AudioPreviewProps {
    url: string;
    accessToken?: string;
}

export const AudioPreview: React.FC<AudioPreviewProps> = ({ url, accessToken }) => {
    const throwError = useAsyncError();

    const isCloudfrontUrl = url.includes("cloudfront.net");

    const serviceWorkerRequired = isCloudfrontUrl && !!accessToken;
    const [serviceWorkerReady, setServiceWorkerReady] = useState(
        !serviceWorkerRequired || !("serviceWorker" in navigator) || !!navigator.serviceWorker.controller
    );

    /* ensures the preview request can be intercepted to handle the authorization concerns
        described below */
    useEffect(() => {
        if (serviceWorkerReady) return;

        const handleControllerChange = () => setServiceWorkerReady(true);
        navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);

        return () => navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    }, [serviceWorkerReady]);

    /* CloudFront preview URLs always have query params, so it's sufficient to
        append another key-value without testing for their presence.
        The sprint-service-worker.js service worker intercepts this request, moves auth-token
        to the Authorization header, and removes it from the URL so the
        CloudFront signed-URL signature remains valid. CloudFront applies its policies
        to each received request before it reaches the authorizer - this includes
        stripping signature parameters present in the `url` string, which results with
        mismatch between the signed target resource path (S3 object proxied via CloudFront)
        and actual request path (including `auth-token` param, which is then treated as a part
        of the resource path - differs from the signature) */
    const targetUrl = isCloudfrontUrl && accessToken ? `${url}&auth-token=${accessToken}` : url;

    if (!serviceWorkerReady) return null;

    return (
        <ReactAudioPlayer
            src={targetUrl}
            autoPlay={false}
            controls={true}
            controlsList="nodownload"
            onError={() => throwError(new PreviewLoadingError())}
        />
    );
};
